'use client';

import cn from '@/utils/cn';
import useEmblaCarousel from 'embla-carousel-react';
import { useEffect } from 'react';

// 활성 탭이 가시영역 왼쪽 끝에 딱 붙지 않도록 두는 여백(px).
// 첫 탭의 좌측 여백(ml-12.5 = 50px)과 동일하게 맞춰, 어느 탭이 앞으로 와도 같은 위치에 놓이게 한다.
const ACTIVE_TAB_START_OFFSET = 20;

interface SubNavTabItem {
  id: string | number;
  label: string;
}

interface SubNavTabProps {
  tabList?: readonly SubNavTabItem[];
  activeId: string | number;
  onTabClick: ({ id }: { id: string | number }) => void;
}

export default function SubNavTab({ tabList, activeId, onTabClick }: SubNavTabProps) {
  const [emblaRef, emblaApi] = useEmblaCarousel({
    dragFree: true,
    // 'start'(offset 0) 대신 고정 offset을 줘서 활성 탭이 가장자리에 붙지 않도록 한다.
    align: () => ACTIVE_TAB_START_OFFSET,
  });
  // 선택된 탭이 가시영역의 첫 번째(좌측 여백만큼 띄운 위치)로 오도록 스크롤한다.
  // 스크롤 여지가 없거나(탭이 다 보이는 경우) 끝쪽 탭이면 embla가 알아서 최대 위치까지만 이동한다.
  //
  // 스크롤을 두 프레임 뒤로 미루는 이유:
  // 탭이 활성화되면 폰트 두께(t14_400 → t14_700)가 바뀌며 슬라이드 폭이 달라지고,
  // embla는 폭 변화(0.5px 이상)를 ResizeObserver로 감지해 reInit한다.
  // reInit은 진행 중인 스크롤 애니메이션을 목적지로 즉시 스냅시켜 순간이동처럼 보이게 만든다.
  // (탭 목록이 늦게 도착해 슬라이드가 추가될 때도 동일하게 reInit이 발생한다.)
  // ResizeObserver 콜백은 다음 페인트 직전에 실행되므로, reInit이 끝난 뒤 애니메이션을 시작한다.
  useEffect(() => {
    if (!emblaApi) return;

    const activeIndex = tabList?.findIndex((tab) => tab.id === activeId) ?? -1;
    if (activeIndex < 0) return;

    let innerFrameId = 0;
    const outerFrameId = requestAnimationFrame(() => {
      innerFrameId = requestAnimationFrame(() => emblaApi.scrollTo(activeIndex));
    });

    return () => {
      cancelAnimationFrame(outerFrameId);
      cancelAnimationFrame(innerFrameId);
    };
  }, [emblaApi, activeId, tabList]);

  return (
    // 헤더가 fixed가 되면서, 헤더 밑에 붙는 이 탭도 sticky로 두면 iOS 하단 오버스크롤 시
    // body 바운스에 끌려 헤더와 똑같이 사라진다. 그래서 탭도 fixed로 고정하고, flow에서 빠진
    // 높이(h-10 desktop:h-14 = sub-nav-tab 40/56)만큼 스페이서로 보정해 기존 레이아웃을 유지한다.
    <>
      <div
        data-fixed-top-bar
        className="fixed inset-x-0 top-header-mobile desktop:top-header-desktop-sub z-header after:absolute after:bottom-0 after:left-0 after:w-full after:h-px after:bg-border-neutral-quaternary bg-white"
      >
        <nav className="flex h-10 desktop:h-14 justify-center">
          <div className="overflow-hidden h-full" ref={emblaRef}>
            <div className="flex h-full gap-6.5 desktop:gap-12.5 select-none">
              {tabList?.map((tab, index) => (
                <button
                  key={tab.id}
                  onClick={() => onTabClick({ id: tab.id })}
                  className={cn(
                    'shrink-0 h-full pb-2.5 desktop:pb-4 pt-2 desktop:pt-3 relative after:absolute after:bottom-0 after:left-1/2 after:h-0.5 after:-translate-x-1/2 after:bg-current after:transition-[width] after:duration-200 after:ease-in-out',
                    activeId === tab.id
                      ? 'font-t14_700 desktop:font-t18_700 text-text-neutral-primary after:w-full'
                      : 'font-t14_400 desktop:font-t18_400 text-text-neutral-tertiary after:w-0 hover:after:w-full',
                    index === 0 && 'ml-6.5 desktop:ml-12.5',
                    index === tabList.length - 1 && 'mr-6.5 desktop:mr-12.5',
                  )}
                >
                  {tab.label}
                </button>
              ))}
            </div>
          </div>
        </nav>
      </div>
      <div aria-hidden className="h-10 desktop:h-14 shrink-0" />
    </>
  );
}
